// src/components/MetricRankingListCard.jsx
import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { DeltaChip } from "./DeltaChip.jsx";
import { splitRankingHalves } from "./rankingUtils.js";
import { useTranslation } from 'react-i18next'
const TONES = {
  indigo: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-indigo-200 dark:border-indigo-900/60",
    sideBar: "bg-indigo-500",
    iconLight: "bg-indigo-100 text-indigo-700",
    iconDark: "bg-indigo-900 text-indigo-100",
    title: "text-indigo-900 dark:text-indigo-100",
    badge:
      "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200",
  },
  emerald: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-emerald-200 dark:border-emerald-900/60",
    sideBar: "bg-emerald-500",
    iconLight: "bg-emerald-100 text-emerald-700",
    iconDark: "bg-emerald-900 text-emerald-100",
    title: "text-emerald-900 dark:text-emerald-100",
    badge:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200",
  },
  amber: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-amber-200 dark:border-amber-900/60",
    sideBar: "bg-amber-500",
    iconLight: "bg-amber-100 text-amber-700",
    iconDark: "bg-amber-900 text-amber-100",
    title: "text-amber-900 dark:text-amber-100",
    badge:
      "bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
  },
  rose: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-rose-200 dark:border-rose-900/60",
    sideBar: "bg-rose-500",
    iconLight: "bg-rose-100 text-rose-700",
    iconDark: "bg-rose-900 text-rose-100",
    title: "text-rose-900 dark:text-rose-100",
    badge:
      "bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
  },
  violet: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-violet-200 dark:border-violet-900/60",
    sideBar: "bg-violet-500",
    iconLight: "bg-violet-100 text-violet-700",
    iconDark: "bg-violet-900 text-violet-100",
    title: "text-violet-900 dark:text-violet-100",
    badge:
      "bg-violet-50 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200",
  },
  slate: {
    cardLight: "bg-gray-50",
    cardDark: "bg-gray-900/60",
    border: "border-slate-200 dark:border-slate-800/60",
    sideBar: "bg-slate-500",
    iconLight: "bg-slate-100 text-slate-700",
    iconDark: "bg-slate-800 text-slate-100",
    title: "text-slate-900 dark:text-slate-100",
    badge:
      "bg-slate-50 text-slate-700 dark:bg-slate-800/40 dark:text-slate-200",
  },
};

export const MetricRankingListCard = ({
  title,
  subtitle,
  icon: Icon,
  players,
  isDarkMode,
  getValue,      // (player) => number
  formatValue,   // (value) => string
  renderExtra,   // (player) => ReactNode
  tone = "slate",

  getDelta,      // (player) => number | null
  deltaIsPercent = false,
  deltaUnit = "",
  deltaDecimals,
  deltaLabel = "vs last session",
  collapsed: collapsedProp,
  onCollapseChange,
  onPlayerClick,
  
}) => {
  const { t } = useTranslation();
  // mode contrôlé / non contrôlé pour le collapse
  const [internalCollapsed, setInternalCollapsed] = React.useState(false);
  const collapsed =
    typeof collapsedProp === "boolean" ? collapsedProp : internalCollapsed;
  const setCollapsed = onCollapseChange || setInternalCollapsed;

  // état "voir tout le classement"
  const [expanded, setExpanded] = React.useState(false);

  const palette = TONES[tone] ?? TONES.slate;
  const textSoft = isDarkMode ? "text-gray-300" : "text-gray-600";

  const entries = React.useMemo(
    () =>
      (players || [])
        .map((p) => {
          const value = getValue(p);
          return Number.isFinite(value) ? { player: p, value } : null;
        })
        .filter(Boolean),
    [players, getValue]
  );

  // Classement principal décroissant (1 = meilleur)
  const sortedDesc = React.useMemo(() => {
    const arr = [...entries].sort((a, b) => b.value - a.value);
    return arr.map((e, index) => ({ ...e, rank: index + 1 }));
  }, [entries]);

  // Split the ranking into two non-overlapping halves.
  const {
    topVisible,
    lowestVisible: flopVisible,
    hasHiddenEntries,
  } = React.useMemo(
    () => splitRankingHalves(sortedDesc, { expanded, collapsedLimit: 3 }),
    [sortedDesc, expanded]
  );

  const renderRow = (entry, index, kind) => {
    const { player, value, rank } = entry;
    const initials = `${player.first_name?.[0] ?? ""}${
      player.last_name?.[0] ?? ""
    }`;
    const muscleInfo = renderExtra ? renderExtra(player) : null;
    const delta = getDelta ? getDelta(player) : null;
    const clickable = !!onPlayerClick;

    return (
      <div
        key={`${kind}-${player.id}-${index}`}
        onClick={() => clickable && onPlayerClick(player)}
        className={`flex flex-col gap-1.5 rounded-xl px-2 py-1 ${
          clickable
            ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            : ""
        }`}
      >
        <div className="flex items-center gap-2">
          {/* Rang global */}
          <div className="w-6 text-[11px] font-semibold text-gray-400 text-right">
            #{rank}
          </div>

          {/* Avatar */}
          <div className="h-8 w-8 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500 to-sky-500 flex items-center justify-center text-xs font-semibold text-white">
            {player.profile_picture ? (
              <img
                src={`data:image/jpeg;base64,${player.profile_picture}`}
                alt={player.first_name + " " + player.last_name}
                className="h-full w-full object-cover"
              />
            ) : (
              initials
            )}
          </div>

          {/* Nom / valeur / delta / date */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="truncate text-xs font-medium">
                {player.first_name} {player.last_name}
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <div className="flex items-center gap-1">
                  <span className="text-[11px] tabular-nums opacity-80">
                    {formatValue(value)}
                  </span>
                  {Number.isFinite(delta) && (
                    <DeltaChip
                      delta={delta}
                      isPercent={deltaIsPercent}
                      unit={deltaUnit}
                      decimals={deltaDecimals}
                      label={deltaLabel}
                    />
                  )}
                </div>
                <div className="text-[10px] opacity-60">
                  {player.timeinfo_date
                    ? `${t('metrics.last_sim')}: ${player.timeinfo_date}`
                    : `${t('metrics.last_sim')}: ${t('common.na')}`}
                </div>
              </div>
            </div>
          </div>
        </div>

        {muscleInfo && (
          <div className={`pl-10 text-[11px] leading-snug ${textSoft}`}>
            {muscleInfo}
          </div>
        )}
      </div>
    );
  };

  const hasData = entries.length > 0;

  return (
    <div
      className={`relative rounded-2xl px-4 py-4 shadow-sm border ${palette.border} ${
        isDarkMode ? palette.cardDark : palette.cardLight
      }`}
    >
      {/* Barre latérale */}
      <div
        className={`absolute inset-y-3 left-0 w-1 rounded-r-full ${palette.sideBar}`}
      />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {Icon && (
            <span
              className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${
                isDarkMode ? palette.iconDark : palette.iconLight
              }`}
            >
              <Icon className="h-5 w-5" />
            </span>
          )}
          <div>
            <div className={`text-sm font-semibold ${palette.title}`}>
              {title}
            </div>
            {subtitle && (
              <div className={`text-xs ${textSoft}`}>{subtitle}</div>
            )}
          </div>
        </div>

        {/* Badge + bouton collapse */}
        <div className="flex items-center gap-2">
          <span
            className={`hidden md:inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${palette.badge}`}
          >
            {t('metric_list_card.team_ranking')}
          </span>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronUp className="h-3 w-3" />
            )}
          </button>
        </div>
      </div>

      {/* Corps */}
      {!collapsed && (
        <>
          {!hasData ? (
            <div className={`text-xs opacity-70 ${textSoft}`}>
              {t('metric_card.no_data')}
            </div>
          ) : (
            <>
              <div className="mb-3 h-4" />

              <div className="mt-2 flex flex-col md:flex-row gap-6">
                {/* TOP */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] uppercase tracking-wide opacity-70">
                      {t('metric_card.top_values')}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {topVisible.map((entry, idx) =>
                      renderRow(entry, idx, "top")
                    )}
                  </div>
                </div>

                {/* Séparateur vertical desktop */}
                <div className="hidden md:block w-px bg-gray-200 dark:bg-gray-700" />

                {/* LOWEST */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] uppercase tracking-wide opacity-70">
                      {t('metric_card.lowest_values')}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {flopVisible.map((entry, idx) =>
                      renderRow(entry, idx, "flop")
                    )}
                  </div>
                </div>
              </div>

              {/* Bouton pour voir tout le classement */}
              {hasHiddenEntries && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                      isDarkMode
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {expanded ? t('metric_card.collapse') : t('metric_card.expand')}
                    {expanded ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
